import type { ContentBlock, ContentLang, ModuleContent, Question } from "./moduleContent";
import {
  applyListChanges,
  blockIdentity,
  classifyChange,
  diffList,
  questionIdentity,
  stableStringify,
  type ChangeStatus,
  type ItemChange,
  type ItemChangeKind,
} from "../../api/_moduleDiff.mjs";

/**
 * Standardmodule in Ebenen: eine Fassung („Snapshot") ist das, was ein
 * Mitarbeiter sieht; eine Ebene („Layer") ist das, was eine Anpassung davon
 * abweicht. Gespeichert werden nur Ebenen – Code < global < Mandant.
 *
 * Seit 2026-09-10 speichert der Editor nur noch die echte Abweichung von der
 * Fassung darunter. Bereiche, die niemand angefasst hat, laufen dadurch mit,
 * wenn SHERLOX AWARE sie ändert. Für die angefassten Bereiche vergleicht der
 * Server Fingerabdrücke (api/_moduleOverride.mjs) und meldet ein Update; die
 * Übernahme einzelner Änderungen passiert hier.
 *
 * Die Regeln zum Übereinanderlegen sind dieselben wie in mergeModuleOverride()
 * (api/_moduleStore.js) und applyModuleOverride() (utils/moduleContent.ts):
 * gesetzte Metadatenfelder gewinnen, Bausteinliste und Quizpool je Sprache
 * werden ganz ersetzt, eine leere Liste ersetzt nichts.
 */

export const CONTENT_LANGS: ContentLang[] = ["de", "en"];
export const META_TEXT_FIELDS = ["title", "shortTitle", "description", "duration", "scenario", "focusAreas"] as const;
export type MetaTextField = (typeof META_TEXT_FIELDS)[number];

export type MetaText = {
  title: string;
  shortTitle: string;
  description: string;
  duration: string;
  scenario: string;
  focusAreas: string[];
};

export type ModuleSnapshot = {
  icon: string;
  color: string;
  level: string;
  /** Videos des Moduls müssen vollständig angesehen werden (components/ForcedVideo.tsx). */
  forceVideo?: boolean;
  meta: Record<ContentLang, MetaText>;
  content: Record<ContentLang, ModuleContent>;
};

export type LayerMeta = {
  icon?: string;
  color?: string;
  level?: string;
  forceVideo?: boolean;
  de?: Partial<MetaText>;
  en?: Partial<MetaText>;
};

export type ModuleLayer = {
  meta: LayerMeta | null;
  content: Partial<Record<ContentLang, Partial<ModuleContent>>> | null;
};

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);


const same = (a: unknown, b: unknown) => stableStringify(a) === stableStringify(b);

/** Rohdaten aus der API (meta/content) als Ebene lesen. */
export function asLayer(raw: unknown): ModuleLayer {
  const r = isObj(raw) ? raw : {};
  return {
    meta: isObj(r.meta) ? (r.meta as LayerMeta) : null,
    content: isObj(r.content) ? (r.content as ModuleLayer["content"]) : null,
  };
}

export function isEmptyLayer(layer: ModuleLayer | null | undefined): boolean {
  return !layer || (!layer.meta && !layer.content);
}

/** Ebene über eine Fassung legen. */
export function applyLayer(base: ModuleSnapshot, layer: ModuleLayer | null | undefined): ModuleSnapshot {
  const next = structuredClone(base);
  next.forceVideo = base.forceVideo === true;
  if (!layer) return next;
  const meta = layer.meta;
  if (meta) {
    if (typeof meta.forceVideo === "boolean") next.forceVideo = meta.forceVideo;
    if (typeof meta.icon === "string" && meta.icon) next.icon = meta.icon;
    if (typeof meta.color === "string" && meta.color) next.color = meta.color;
    if (typeof meta.level === "string" && meta.level) next.level = meta.level;
    for (const lang of CONTENT_LANGS) {
      const text = meta[lang];
      if (!isObj(text)) continue;
      for (const field of META_TEXT_FIELDS) {
        const value = (text as Record<string, unknown>)[field];
        if (field === "focusAreas") {
          if (Array.isArray(value) && value.length > 0) next.meta[lang].focusAreas = value.map(String);
        } else if (typeof value === "string" && value) {
          next.meta[lang][field] = value;
        }
      }
    }
  }
  const content = layer.content;
  if (content) {
    for (const lang of CONTENT_LANGS) {
      const c = content[lang];
      if (!isObj(c)) continue;
      if (Array.isArray(c.blocks) && c.blocks.length > 0) next.content[lang].blocks = structuredClone(c.blocks);
      if (Array.isArray(c.questions) && c.questions.length > 0) next.content[lang].questions = structuredClone(c.questions);
    }
  }
  return next;
}

/** Komplette Fassung als Ebene – so geht der Bezugspunkt an den Server. */
export function snapshotToLayer(s: ModuleSnapshot): ModuleLayer {
  return {
    meta: { icon: s.icon, color: s.color, level: s.level, forceVideo: s.forceVideo === true, de: { ...s.meta.de }, en: { ...s.meta.en } },
    content: { de: structuredClone(s.content.de), en: structuredClone(s.content.en) },
  };
}

/**
 * Nur das, was `edited` von `upstream` unterscheidet. Leere Felder und Listen
 * fallen auf die Fassung darunter zurück (der Server nimmt sie ohnehin nicht an).
 */
export function layerDiff(edited: ModuleSnapshot, upstream: ModuleSnapshot, opts: { includeLevel: boolean }): ModuleLayer {
  const meta: LayerMeta = {};
  const icon = edited.icon.trim();
  if (icon && icon !== upstream.icon.trim()) meta.icon = icon;
  const color = edited.color.trim();
  if (color && color.toLowerCase() !== upstream.color.trim().toLowerCase()) meta.color = color;
  if (opts.includeLevel && edited.level && edited.level !== upstream.level) meta.level = edited.level;
  // Auch „aus" wird gespeichert, wenn die Fassung darunter „an" sagt.
  if ((edited.forceVideo === true) !== (upstream.forceVideo === true)) meta.forceVideo = edited.forceVideo === true;
  for (const lang of CONTENT_LANGS) {
    const text: Partial<MetaText> = {};
    for (const field of META_TEXT_FIELDS) {
      if (field === "focusAreas") {
        const mine = edited.meta[lang].focusAreas.map(s => s.trim()).filter(Boolean);
        const theirs = upstream.meta[lang].focusAreas.map(s => s.trim()).filter(Boolean);
        if (mine.length > 0 && !same(mine, theirs)) text.focusAreas = mine;
      } else {
        const mine = edited.meta[lang][field].trim();
        if (mine && mine !== upstream.meta[lang][field].trim()) text[field] = mine;
      }
    }
    if (Object.keys(text).length > 0) meta[lang] = text;
  }

  const content: Partial<Record<ContentLang, ModuleContent>> = {};
  for (const lang of CONTENT_LANGS) {
    const mine = edited.content[lang];
    const theirs = upstream.content[lang];
    const blocks = mine.blocks.length > 0 && !same(mine.blocks, theirs.blocks) ? mine.blocks : [];
    const questions = mine.questions.length > 0 && !same(mine.questions, theirs.questions) ? mine.questions : [];
    if (blocks.length > 0 || questions.length > 0) content[lang] = { blocks, questions };
  }

  return {
    meta: Object.keys(meta).length > 0 ? meta : null,
    content: Object.keys(content).length > 0 ? content : null,
  };
}

// ── Bereiche ──────────────────────────────────────────────────────────────

const META_FIELD_LABELS: Record<MetaTextField, string> = {
  title: "Titel",
  shortTitle: "Kurztitel",
  description: "Beschreibung",
  duration: "Dauer",
  scenario: "Praxisfall",
  focusAreas: "Schwerpunkte",
};

/** Bereiche, die eine Ebene überdeckt – dieselbe Einteilung wie upstreamPartKeys() im Server. */
export function layerPartKeys(layer: ModuleLayer | null | undefined): string[] {
  const keys: string[] = [];
  const meta = layer?.meta;
  if (meta) {
    for (const key of ["icon", "color", "level"] as const) if (meta[key]) keys.push(key);
    for (const lang of CONTENT_LANGS) {
      const text = meta[lang];
      if (!text) continue;
      for (const field of META_TEXT_FIELDS) if (text[field] !== undefined) keys.push(`${lang}.${field}`);
    }
  }
  const content = layer?.content;
  if (content) {
    for (const lang of CONTENT_LANGS) {
      const c = content[lang];
      if (!c) continue;
      if (Array.isArray(c.blocks) && c.blocks.length > 0) keys.push(`${lang}.blocks`);
      if (Array.isArray(c.questions) && c.questions.length > 0) keys.push(`${lang}.questions`);
    }
  }
  return keys;
}

export function partLabel(key: string): string {
  if (key === "icon") return "Icon";
  if (key === "color") return "Farbe";
  if (key === "level") return "Stufe";
  const [lang, field] = key.split(".");
  const suffix = ` (${(lang ?? "").toUpperCase()})`;
  if (field === "blocks") return `Lernblöcke${suffix}`;
  if (field === "questions") return `Quiz${suffix}`;
  return `${META_FIELD_LABELS[field as MetaTextField] ?? field}${suffix}`;
}

/** Nur die genannten Bereiche einer Fassung – für einen schlanken Abgleich. */
export function pickParts(s: ModuleSnapshot, keys: string[]): ModuleLayer {
  const layer: ModuleLayer = { meta: {}, content: {} };
  const meta = layer.meta as LayerMeta;
  const content = layer.content as Partial<Record<ContentLang, Partial<ModuleContent>>>;
  for (const key of keys) {
    if (key === "icon" || key === "color" || key === "level") { meta[key] = s[key]; continue; }
    const [lang, field] = key.split(".") as [ContentLang, string];
    if (!CONTENT_LANGS.includes(lang)) continue;
    if (field === "blocks" || field === "questions") {
      const c = content[lang] ?? (content[lang] = { blocks: [], questions: [] });
      c[field] = structuredClone(s.content[lang][field]) as never;
    } else if ((META_TEXT_FIELDS as readonly string[]).includes(field)) {
      const text = meta[lang] ?? (meta[lang] = {});
      (text as Record<string, unknown>)[field] = structuredClone(s.meta[lang][field as MetaTextField]);
    }
  }
  return layer;
}

// ── Listenvergleich (Bausteine, Fragen) ───────────────────────────────────
// Liegt in api/_moduleDiff.mjs, weil der Server dieselbe Logik für
// Pflicht-Updates braucht (api/ga.js). Hier nur typisiert weitergereicht.
export {
  applyListChanges,
  blockIdentity,
  classifyChange,
  diffList,
  questionIdentity,
  stableStringify,
};
export type { ChangeStatus, ItemChange, ItemChangeKind };
const sameBlockKind = (a: ContentBlock, b: ContentBlock) => a.type === b.type;
const anyQuestion = () => true;

// ── Pflicht-Update (GA-Editor) ────────────────────────────────────────────

/** Ein geänderter Bereich für ein Pflicht-Update – Gegenstück zu sanitizeMandatoryParts() im Server. */
export type MandatoryPart = { key: string; next?: unknown[]; changes?: ItemChange<unknown>[] };

/**
 * Was sich zwischen der bisher ausgelieferten Fassung (`before`, Code + alte
 * globale Fassung) und der neuen (`after`) geändert hat. Stammdaten nur als
 * Schlüssel, Bausteine/Fragen mit der neuen Liste und den einzelnen Änderungen –
 * der Server spielt genau diese in die Kunden-Anpassungen ein (api/ga.js).
 */
export function mandatoryParts(before: ModuleSnapshot, after: ModuleSnapshot): MandatoryPart[] {
  const parts: MandatoryPart[] = [];
  for (const key of ["icon", "color", "level"] as const) if (!same(before[key], after[key])) parts.push({ key });
  for (const lang of CONTENT_LANGS) {
    for (const field of META_TEXT_FIELDS) {
      if (!same(before.meta[lang][field], after.meta[lang][field])) parts.push({ key: `${lang}.${field}` });
    }
    const b = before.content[lang];
    const a = after.content[lang];
    if (!same(b.blocks, a.blocks)) {
      parts.push({ key: `${lang}.blocks`, next: a.blocks, changes: diffList(b.blocks, a.blocks, blockIdentity, sameBlockKind) });
    }
    if (!same(b.questions, a.questions)) {
      parts.push({ key: `${lang}.questions`, next: a.questions, changes: diffList(b.questions, a.questions, questionIdentity, anyQuestion) });
    }
  }
  return parts;
}

// ── Übernahme-Ansicht ─────────────────────────────────────────────────────

type MetaValue = string | string[];

export type ReviewChange =
  | { id: string; area: "meta"; part: string; before: MetaValue; after: MetaValue; mine: MetaValue; status: ChangeStatus }
  | { id: string; area: "blocks"; part: string; lang: ContentLang; change: ItemChange<ContentBlock>; status: ChangeStatus }
  | { id: string; area: "questions"; part: string; lang: ContentLang; change: ItemChange<Question>; status: ChangeStatus };

function metaPartValue(s: ModuleSnapshot, key: string): MetaValue {
  if (key === "icon" || key === "color" || key === "level") return s[key] ?? "";
  const [lang, field] = key.split(".") as [ContentLang, MetaTextField];
  const value = s.meta[lang]?.[field];
  return Array.isArray(value) ? value : (value ?? "");
}

/**
 * Was hat sich darunter von `base` (Bezugspunkt beim Speichern) zu `now`
 * (aktuelle Fassung) geändert – beschränkt auf die Bereiche `parts`, die die
 * eigene Anpassung überdeckt – und wie trifft das auf die eigene Fassung `mine`?
 */
export function buildReview(base: ModuleSnapshot, now: ModuleSnapshot, mine: ModuleSnapshot, parts: string[]): ReviewChange[] {
  const out: ReviewChange[] = [];
  for (const part of parts) {
    const [lang, field] = part.split(".") as [ContentLang, string];
    if (field === "blocks") {
      for (const change of diffList(base.content[lang].blocks, now.content[lang].blocks, blockIdentity, sameBlockKind)) {
        const status = classifyChange(change, mine.content[lang].blocks, blockIdentity);
        if (status !== "done") out.push({ id: `${part}:${change.id}`, area: "blocks", part, lang, change, status });
      }
    } else if (field === "questions") {
      for (const change of diffList(base.content[lang].questions, now.content[lang].questions, questionIdentity, anyQuestion)) {
        const status = classifyChange(change, mine.content[lang].questions, questionIdentity);
        if (status !== "done") out.push({ id: `${part}:${change.id}`, area: "questions", part, lang, change, status });
      }
    } else {
      const before = metaPartValue(base, part);
      const after = metaPartValue(now, part);
      const current = metaPartValue(mine, part);
      if (same(before, after) || same(current, after)) continue;
      out.push({ id: part, area: "meta", part, before, after, mine: current, status: same(current, before) ? "clean" : "conflict" });
    }
  }
  return out;
}

/** Ausgewählte Änderungen in die eigene Fassung übernehmen. */
export function applyReview(mine: ModuleSnapshot, now: ModuleSnapshot, selected: ReviewChange[]): ModuleSnapshot {
  const next = structuredClone(mine);
  const byPart = new Map<string, ReviewChange[]>();
  for (const change of selected) byPart.set(change.part, [...(byPart.get(change.part) ?? []), change]);
  for (const [part, changes] of byPart) {
    const [lang, field] = part.split(".") as [ContentLang, string];
    if (field === "blocks") {
      next.content[lang].blocks = applyListChanges(
        next.content[lang].blocks, now.content[lang].blocks,
        changes.flatMap(c => (c.area === "blocks" ? [c.change] : [])), blockIdentity,
      );
    } else if (field === "questions") {
      next.content[lang].questions = applyListChanges(
        next.content[lang].questions, now.content[lang].questions,
        changes.flatMap(c => (c.area === "questions" ? [c.change] : [])), questionIdentity,
      );
    } else {
      const change = changes[0];
      if (change.area !== "meta") continue;
      if (part === "icon" || part === "color" || part === "level") {
        next[part] = String(change.after);
      } else if (field === "focusAreas") {
        next.meta[lang].focusAreas = Array.isArray(change.after) ? [...change.after] : [];
      } else {
        next.meta[lang][field as Exclude<MetaTextField, "focusAreas">] = String(change.after);
      }
    }
  }
  return next;
}

/** Bereiche, in denen sich zwei Fassungen unterscheiden (für „Vergleichen" ohne Bezugspunkt). */
export function differingParts(a: ModuleSnapshot, b: ModuleSnapshot, candidates: string[]): string[] {
  return candidates.filter(part => {
    const [lang, field] = part.split(".") as [ContentLang, string];
    if (field === "blocks" || field === "questions") return !same(a.content[lang][field], b.content[lang][field]);
    return !same(metaPartValue(a, part), metaPartValue(b, part));
  });
}

/** Kurzbeschreibung des Unterschieds zweier Fassungen, z. B. „DE: 1 Baustein neu, 2 geändert". */
export function summarizeDifference(from: ModuleSnapshot, to: ModuleSnapshot): string[] {
  const lines: string[] = [];
  const count = (changes: ItemChange<unknown>[], noun: [string, string]) => {
    const n = (kind: ItemChangeKind) => changes.filter(c => c.kind === kind).length;
    const parts: string[] = [];
    const plural = (x: number) => (x === 1 ? noun[0] : noun[1]);
    if (n("added")) parts.push(`${n("added")} ${plural(n("added"))} neu`);
    if (n("changed")) parts.push(`${n("changed")} geändert`);
    if (n("removed")) parts.push(`${n("removed")} entfernt`);
    if (n("moved")) parts.push(`${n("moved")} verschoben`);
    return parts.join(", ");
  };
  const metaChanged: string[] = [];
  for (const key of ["icon", "color", "level"]) if (!same(metaPartValue(from, key), metaPartValue(to, key))) metaChanged.push(partLabel(key));
  if ((from.forceVideo === true) !== (to.forceVideo === true)) metaChanged.push("Video-Pflicht");
  for (const lang of CONTENT_LANGS) {
    for (const field of META_TEXT_FIELDS) {
      const key = `${lang}.${field}`;
      if (!same(metaPartValue(from, key), metaPartValue(to, key))) metaChanged.push(partLabel(key));
    }
    const blocks = count(diffList(from.content[lang].blocks, to.content[lang].blocks, blockIdentity, sameBlockKind), ["Baustein", "Bausteine"]);
    if (blocks) lines.push(`${lang.toUpperCase()}-Lernblöcke: ${blocks}`);
    const questions = count(diffList(from.content[lang].questions, to.content[lang].questions, questionIdentity, anyQuestion), ["Frage", "Fragen"]);
    if (questions) lines.push(`${lang.toUpperCase()}-Quiz: ${questions}`);
  }
  if (metaChanged.length) lines.unshift(`Stammdaten: ${metaChanged.join(", ")}`);
  return lines;
}

// ── Pflichtfelder (Spiegel der Server-Regeln, nur für die Anzeige) ─────────
// Maßgeblich bleibt sanitizeModuleOverride() in api/_moduleOverride.mjs – das
// ist die Sicherheitsgrenze. Hier steht nur, was der Editor vorher sagen soll,
// damit beim Speichern nichts mehr still verschwindet.

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const PDF_PATH = /^\/dokumente\/[A-Za-z0-9._-]+\.pdf$/;
// Hochgeladene Datei: <tenantId|platform>/<modul>/<datei>. Welcher Ordner erlaubt
// ist, prüft der Server (safeAssetPath in api/_moduleOverride.mjs).
const ASSET_PATH = /^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+\.(pdf|png|jpe?g|webp|gif|svg)$/i;
// http wird wie im Server auf https gehoben.
const isWebUrl = (value: string) => /^https?:\/\/[^\s]+$/i.test(value);

export type Issues = {
  /** Der ganze Baustein bzw. die ganze Frage wird nicht gespeichert. */
  fatal: string[];
  /** Gespeichert wird, aber ohne diese Teile bzw. mit Ersatzwert. */
  dropped: string[];
};

const t = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const badColor = (value: unknown) => t(value) !== "" && !HEX_COLOR.test(t(value));

export function blockIssues(block: ContentBlock): Issues {
  const fatal: string[] = [];
  const dropped: string[] = [];
  if (!t(block.title)) fatal.push("Überschrift fehlt");
  const tooMany = (count: number, max: number, noun: string) => {
    if (count > max) dropped.push(`nur die ersten ${max} ${noun} werden gespeichert`);
  };
  switch (block.type) {
    case "stats": {
      const valid = block.items.filter(it => t(it.value) && t(it.label));
      if (valid.length === 0) fatal.push("keine Kachel mit Wert und Beschriftung");
      else if (valid.length < block.items.length) dropped.push(`${block.items.length - valid.length} Kachel(n) ohne Wert oder Beschriftung`);
      tooMany(valid.length, 12, "Kacheln");
      if (block.items.some(it => badColor(it.color))) dropped.push("ungültige Kachelfarbe wird durch Blau ersetzt");
      break;
    }
    case "keypoints": {
      const valid = block.items.filter(it => t(it.title));
      if (valid.length === 0) fatal.push("kein Punkt mit Titel");
      else if (valid.length < block.items.length) dropped.push(`${block.items.length - valid.length} Punkt(e) ohne Titel`);
      tooMany(valid.length, 20, "Punkte");
      break;
    }
    case "checklist": {
      const valid = block.items.filter(it => t(it.label));
      if (valid.length === 0) fatal.push("kein Punkt mit Text");
      else if (valid.length < block.items.length) dropped.push(`${block.items.length - valid.length} Punkt(e) ohne Text`);
      tooMany(valid.length, 20, "Punkte");
      break;
    }
    case "warning": {
      const valid = block.items.filter(it => t(it.text));
      if (valid.length === 0) fatal.push("keine Warnung mit Text");
      else if (valid.length < block.items.length) dropped.push(`${block.items.length - valid.length} Warnung(en) ohne Text`);
      tooMany(valid.length, 20, "Warnungen");
      break;
    }
    case "comparison": {
      const left = block.left.items.filter(x => t(x)).length;
      const right = block.right.items.filter(x => t(x)).length;
      if (left === 0 && right === 0) fatal.push("beide Spalten sind leer");
      if (left > 12 || right > 12) dropped.push("je Spalte werden nur 12 Einträge gespeichert");
      if (badColor(block.left.color) || badColor(block.right.color)) dropped.push("ungültige Spaltenfarbe wird ersetzt");
      break;
    }
    case "scenario": {
      if (!t(block.situation)) fatal.push("Situation fehlt");
      const valid = block.options.filter(o => t(o.text));
      if (valid.length < 2) fatal.push("mindestens zwei Antworten mit Text nötig");
      else if (valid.length < block.options.length) dropped.push(`${block.options.length - valid.length} Antwort(en) ohne Text`);
      tooMany(valid.length, 6, "Antworten");
      if (valid.length >= 2 && !valid.some(o => o.correct)) dropped.push("keine richtige Antwort markiert – die erste gilt als richtig");
      break;
    }
    case "cascade": {
      const valid = block.steps.filter(st => t(st.label));
      if (valid.length < 2) fatal.push("mindestens zwei Stufen mit Titel nötig");
      else if (valid.length < block.steps.length) dropped.push(`${block.steps.length - valid.length} Stufe(n) ohne Titel`);
      tooMany(valid.length, 10, "Stufen");
      break;
    }
    case "assign": {
      const validQ = block.questions.filter(q => {
        const options = q.options.map(o => t(o)).filter(Boolean);
        return t(q.question) && options.length >= 2 && options.includes(t(q.correct));
      });
      if (validQ.length < block.questions.length) {
        dropped.push(`${block.questions.length - validQ.length} Frage(n) unvollständig – die richtige Antwort muss wörtlich eine der Möglichkeiten sein`);
      }
      const validG = block.groups.filter(g => t(g.label) && g.people.some(p => t(p.name)));
      if (validG.length < block.groups.length) dropped.push(`${block.groups.length - validG.length} Gruppe(n) ohne Namen oder Einträge`);
      const namelessPeople = block.groups.reduce((n, g) => n + g.people.filter(p => !t(p.name)).length, 0);
      if (namelessPeople > 0) dropped.push(`${namelessPeople} Eintrag/Einträge ohne Name`);
      if (validQ.length === 0 && validG.length === 0) fatal.push("weder eine vollständige Frage noch eine Gruppe");
      break;
    }
    case "tip": {
      if (!t(block.content)) fatal.push("Text fehlt");
      if (badColor(block.color)) dropped.push("ungültige Farbe wird durch Violett ersetzt");
      break;
    }
    case "pdf": {
      if (!PDF_PATH.test(t(block.file)) && !ASSET_PATH.test(t(block.file))) fatal.push("keine gültige Datei – PDF hochladen oder /dokumente/<name>.pdf angeben");
      break;
    }
    case "image": {
      const src = t(block.src);
      if (!src) fatal.push("kein Bild hinterlegt");
      else if (!isWebUrl(src) && !ASSET_PATH.test(src)) fatal.push("Bild muss hochgeladen oder eine https-Adresse sein");
      break;
    }
    case "video": {
      if (!t(block.url)) fatal.push("keine Video-Adresse");
      else if (!isWebUrl(t(block.url))) fatal.push("Video-Adresse muss mit https:// beginnen");
      break;
    }
    case "link": {
      if (!t(block.url)) fatal.push("keine Adresse");
      else if (!isWebUrl(t(block.url))) fatal.push("Adresse muss mit https:// beginnen");
      break;
    }
    default:
      break;
  }
  return { fatal, dropped };
}

export function questionIssues(q: Question): Issues {
  const fatal: string[] = [];
  const dropped: string[] = [];
  if (!t(q.question)) fatal.push("Fragetext fehlt");
  if (!t(q.correct)) fatal.push("richtige Antwort fehlt");
  const wrong = q.wrong.filter(w => t(w));
  if (wrong.length === 0) fatal.push("mindestens eine falsche Antwort nötig");
  if (wrong.length > 5) dropped.push("nur die ersten 5 falschen Antworten werden gespeichert");
  return { fatal, dropped };
}

/** Alles, was beim Speichern von `layer` nicht so ankäme, wie es im Editor steht. */
export function collectSaveIssues(layer: ModuleLayer): string[] {
  const lines: string[] = [];
  if (layer.meta?.color && !HEX_COLOR.test(layer.meta.color)) lines.push(`Farbe „${layer.meta.color}" ist kein Hex-Wert (#RRGGBB) – wird nicht gespeichert`);
  for (const lang of CONTENT_LANGS) {
    const c = layer.content?.[lang];
    if (!c) continue;
    (c.blocks ?? []).forEach((block, i) => {
      const { fatal, dropped } = blockIssues(block);
      const name = `${lang.toUpperCase()} · Baustein ${i + 1}${block.title ? ` „${block.title}"` : ""}`;
      if (fatal.length) lines.push(`${name}: ${fatal.join(", ")} → wird nicht gespeichert`);
      else if (dropped.length) lines.push(`${name}: ${dropped.join(", ")}`);
    });
    (c.questions ?? []).forEach((q, i) => {
      const { fatal, dropped } = questionIssues(q);
      const name = `${lang.toUpperCase()} · Frage ${i + 1}`;
      if (fatal.length) lines.push(`${name}: ${fatal.join(", ")} → wird nicht gespeichert`);
      else if (dropped.length) lines.push(`${name}: ${dropped.join(", ")}`);
    });
  }
  return lines;
}

export const isHexColor = (value: string) => HEX_COLOR.test(value.trim());
